import {
  FullScreenControl,
  SigmaContainer,
  ZoomControl,
} from "@react-sigma/core";
import { createNodeImageProgram } from "@sigma/node-image";
import { MultiDirectedGraph } from "graphology";
import { constant, keyBy, mapValues, omit } from "lodash";
import { FC, useEffect, useMemo, useState } from "react";
import { BiBookContent, BiRadioCircleMarked } from "react-icons/bi";
import {
  BsArrowsFullscreen,
  BsFullscreenExit,
  BsZoomIn,
  BsZoomOut,
} from "react-icons/bs";
import { GrClose } from "react-icons/gr";
import { Settings } from "sigma/settings";
import { circular } from "graphology-layout";

import { drawHover, drawLabel } from "../canvas-utils";
import { Dataset, FiltersState } from "../types";
import ClustersPanel from "./ClustersPanel";
import DescriptionPanel from "./DescriptionPanel";
//import DataPanel from "./DataPanel";
import GraphDataController from "./GraphDataController";
import GraphEventsController from "./GraphEventsController";
import GraphSettingsController from "./GraphSettingsController";
import GraphTitle from "./GraphTitle";
import SearchField from "./SearchField";
import TagsPanel from "./TagsPanel";
import LayoutPanel from "./LayoutPanel";
import "../styles.css";

const Root: FC = () => {
  const graph = useMemo(() => new MultiDirectedGraph(), []);
  const [showContents, setShowContents] = useState(false);
  const [dataReady, setDataReady] = useState(false);
  const [dataset, setDataset] = useState<Dataset | null>(null);
  const [filtersState, setFiltersState] = useState<FiltersState>({
    clusters: {},
    tags: {},
    datagroups: {},
  });
  const [hoveredNode, setHoveredNode] = useState<string | null>(null);
  const [clickedNode, setClickedNode] = useState<string | null>(null);
  const sigmaSettings: Partial<Settings> = useMemo(
    () => ({
      nodeProgramClasses: {
        image: createNodeImageProgram({
          size: { mode: "force", value: 256 },
        }),
      },
      defaultDrawNodeLabel: drawLabel,
      defaultDrawNodeHover: drawHover,
      defaultNodeType: "image",
      defaultEdgeType: "arrow",
      labelDensity: 0.07,
      labelGridCellSize: 60,
      labelRenderedSizeThreshold: 15,
      labelFont: "Lato, sans-serif",
      zIndex: true,
    }),
    []
  );

  // Load data on mount:
  useEffect(() => {
    fetch("./datasets/completeSynthDataset.json")
      .then((res) => res.json())
      .then((dataset: Dataset) => {
        const tags = keyBy(dataset.tags, "key");
        const clusters = keyBy(dataset.clusters, "key");

        // Generate colors for tags
        const tagColors = mapValues(tags, (_, tagKey) => {
          const colors = [
            "#1f77b4",
            "#ff7f0e",
            "#2ca02c",
            "#d62728",
            "#9467bd",
            "#8c564b",
            "#e377c2",
            "#7f7f7f",
            "#bcbd22",
            "#17becf",
          ];
          const tagIndex = Object.keys(tags).indexOf(tagKey);
          return colors[tagIndex % colors.length];
        });

        dataset.nodes.forEach((node) => {
          graph.addNode(node.key, {
            ...node,
            color: tagColors[node.tag],
            original_x: node.x, // Store original coordinates
            original_y: node.y,
            clusterLabel: clusters[node.cluster]?.clusterLabel || "", // Add cluster label
          });
        });
        dataset.edges.forEach((new_edge) => {
          const source = new_edge["source"];
          const target = new_edge["target"];
          graph.addEdge(source, target, { size: 1 });
        });

        // Use degrees as node sizes:
        const scores = graph
          .nodes()
          .map((node) => graph.getNodeAttribute(node, "score"));
        const minDegree = Math.min(...scores);
        const maxDegree = Math.max(...scores);
        const MIN_NODE_SIZE = 5;
        const MAX_NODE_SIZE = 15;
        graph.forEachNode((node) =>
          graph.setNodeAttribute(
            node,
            "size",
            ((graph.getNodeAttribute(node, "score") - minDegree) /
              (maxDegree - minDegree)) *
              (MAX_NODE_SIZE - MIN_NODE_SIZE) +
              MIN_NODE_SIZE
          )
        );

        setFiltersState({
          clusters: mapValues(keyBy(dataset.clusters, "key"), constant(true)),
          tags: mapValues(keyBy(dataset.tags, "key"), constant(true)),
          datagroups: mapValues(
            keyBy(dataset.datagroups, "key"),
            constant(true)
          ),
        });
        setDataset(dataset);
        requestAnimationFrame(() => {
          setDataReady(true);
          // Calculate scale dynamically based on number of nodes
          const numNodes = graph.nodes().length;
          // Much larger base scale and per-node increment for more spacing
          const scale = 2000 + numNodes * 500;
          // Initialize with circular layout
          const circularPositions = circular(graph, { scale });
          graph.forEachNode((node) => {
            graph.setNodeAttribute(node, "x", circularPositions[node].x);
            graph.setNodeAttribute(node, "y", circularPositions[node].y);
          });
        });
      });
  }, []);

  if (!dataset) return null;

  return (
    <div id="app-root" className={showContents ? "show-contents" : ""}>
      <SigmaContainer
        graph={graph}
        settings={sigmaSettings}
        className="react-sigma"
      >
        <GraphSettingsController
          hoveredNode={hoveredNode}
          clickedNode={clickedNode}
        />
        <GraphEventsController
          setHoveredNode={setHoveredNode}
          setClickedNode={setClickedNode}
          clickedNode={clickedNode}
        />
        <GraphDataController filters={filtersState} />

        {dataReady && (
          <>
            <div className="controls">
              <div className="react-sigma-control ico">
                <button
                  type="button"
                  className="show-contents"
                  onClick={() => setShowContents(true)}
                  title="Show caption and description"
                >
                  <BiBookContent />
                </button>
              </div>
              <FullScreenControl className="ico">
                <BsArrowsFullscreen />
                <BsFullscreenExit />
              </FullScreenControl>

              <ZoomControl className="ico">
                <BsZoomIn />
                <BsZoomOut />
                <BiRadioCircleMarked />
              </ZoomControl>
            </div>
            <div className="contents">
              <div className="ico">
                <button
                  type="button"
                  className="ico hide-contents"
                  onClick={() => setShowContents(false)}
                  title="Show caption and description"
                >
                  <GrClose />
                </button>
              </div>
              <GraphTitle filters={filtersState} />
              <div className="panels">
                <SearchField filters={filtersState} />
                <DescriptionPanel />
                <ClustersPanel
                  clusters={dataset.clusters}
                  filters={filtersState}
                  setClusters={(clusters) =>
                    setFiltersState((filters) => ({
                      ...filters,
                      clusters,
                    }))
                  }
                  toggleCluster={(cluster) => {
                    setFiltersState((filters) => ({
                      ...filters,
                      clusters: filters.clusters[cluster]
                        ? omit(filters.clusters, cluster)
                        : { ...filters.clusters, [cluster]: true },
                    }));
                  }}
                />
                <TagsPanel
                  tags={dataset.tags}
                  filters={filtersState}
                  setTags={(tags) =>
                    setFiltersState((filters) => ({
                      ...filters,
                      tags,
                    }))
                  }
                  toggleTag={(tag) => {
                    setFiltersState((filters) => ({
                      ...filters,
                      tags: filters.tags[tag]
                        ? omit(filters.tags, tag)
                        : { ...filters.tags, [tag]: true },
                    }));
                  }}
                />
                {/* <DataPanel
                  datagroups={dataset.datagroups}
                  filters={filtersState}
                  setDatagroup={(datagroups) =>
                    setFiltersState((filters) => ({
                      ...filters,
                      datagroups,
                    }))
                  }
                  toggleDatagroup={(datagroup) => {
                    setFiltersState((filters) => ({
                      ...filters,
                      datagroups: filters.datagroups[datagroup]
                        ? omit(filters.datagroups, datagroup)
                        : { ...filters.datagroups, [datagroup]: true },
                    }));
                  }}
                />*/}
                <LayoutPanel />
              </div>
            </div>
          </>
        )}
      </SigmaContainer>
    </div>
  );
};

export default Root;
